import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/seo/Seo';
import { breadcrumbSchema } from '../components/seo/jsonld';

const AboutPage = () => (
    <>
        <Seo
            title="About Us"
            description="About pdfconvertor.shop — who we are, why we built a privacy-first PDF converter, and how the tools work."
            path="/about"
            jsonLd={breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'About', path: '/about' },
            ])}
        />

        <div className="page-container narrow">
            <article>
                <header className="page-header">
                    <h1>About pdfconvertor.shop</h1>
                    <p className="page-lead">
                        A small, independent project building private, browser-based tools
                        for everyday document work.
                    </p>
                </header>

                <section className="content-section">
                    <h2>Why this site exists</h2>
                    <p>
                        Most online document converters work by asking you to upload your file,
                        processing it on a server, and letting you download the result. It
                        works, but it means every converter on the internet has — at some point
                        — had a copy of your document on its infrastructure. For something like
                        a meme image, that's fine. For a signed contract, a tax form, or a
                        diagnostic medical report, it's not.
                    </p>
                    <p>
                        pdfconvertor.shop was built on a different premise: modern browsers are
                        powerful enough to do the conversion themselves. By moving the work to
                        your device, we eliminate an entire class of privacy concerns in one
                        architectural decision. The trade-off is that we can't offer every
                        feature a server-side tool can — OCR on scanned PDFs, for instance —
                        but for everyday document conversions, it's a better deal.
                    </p>
                </section>

                <section className="content-section">
                    <h2>What we build</h2>
                    <p>The site currently offers these browser-based tools:</p>
                    <ul>
                        <li><Link to="/word-to-pdf">Word to PDF</Link> — convert <code>.docx</code> documents to PDF.</li>
                        <li><Link to="/image-to-pdf">Image to PDF</Link> — put JPG and PNG images into a PDF.</li>
                        <li><Link to="/compress-pdf">Compress PDF</Link> — shrink PDF file size without quality loss.</li>
                        <li><Link to="/pdf-to-image">PDF to Image</Link> — export every page as a PNG image.</li>
                        <li><Link to="/pdf-to-word">PDF to Word</Link> — extract text into a <code>.docx</code>.</li>
                    </ul>
                    <p>
                        Each tool's page explains what the tool is good at, what it's honestly
                        not good at, and what to expect in the output. We'd rather tell you the
                        truth than oversell.
                    </p>
                </section>

                <section className="content-section">
                    <h2>How the tools work</h2>
                    <p>
                        Every conversion uses mature open-source libraries running as
                        JavaScript or WebAssembly inside your browser:
                    </p>
                    <ul>
                        <li><code>pdf-lib</code> for creating, editing, and compressing PDFs.</li>
                        <li><code>pdf.js</code> (from Mozilla) for rendering and reading PDFs.</li>
                        <li><code>mammoth</code> for reading Word <code>.docx</code> files.</li>
                        <li><code>jsPDF</code> for generating PDFs from scratch.</li>
                        <li><code>docx</code> for writing Word documents.</li>
                        <li><code>jszip</code> and <code>file-saver</code> for bundling and downloads.</li>
                    </ul>
                    <p>
                        When you pick a file, the browser reads it into memory, the relevant
                        library processes it on your CPU, and the output is written directly to
                        your downloads folder. At no point during conversion does the file
                        travel to our servers.
                    </p>
                </section>

                <section className="content-section">
                    <h2>How we pay for the site</h2>
                    <p>
                        Hosting a website has real costs, even a small one. pdfconvertor.shop is
                        funded by ads served through <strong>Google AdSense</strong>. Ads appear
                        in defined positions — they don't block the tools, and they don't
                        interrupt conversions. We don't sell a premium tier, and we don't sell
                        data, because there's no data to sell.
                    </p>
                    <p>
                        If you want to understand what data flows through the site (basically:
                        what your browser tells any website it visits), please read our{' '}
                        <Link to="/privacy-policy">Privacy Policy</Link>.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Contact</h2>
                    <p>
                        Found a bug? Want a feature? Reach us via the{' '}
                        <Link to="/contact">contact page</Link>, or email{' '}
                        <a href="mailto:pdf2convertor@gmail.com">pdf2convertor@gmail.com</a>.
                    </p>
                </section>
            </article>
        </div>
    </>
);

export default AboutPage;
